/**
 * Kontrak data halaman admin AI Assistant (`/admin/ai-assistant`).
 * Dipakai UI sekarang dan endpoint `/api/admin/ai-assistant/*` di tahap P3
 * (lihat docs-local/plans/ai-assistant/03-pondasi.md §9–10). API key tidak
 * pernah ada di respons — hanya `hasApiKey` + `apiKeyHint`.
 */

import type { ProviderModeStatusDto } from "./ai-assistant-provider-mode";
import type { WallNocMicMode, WallVoiceMode } from "./ai-assistant-voice";

/**
 * Urutan kartu di halaman admin. `chat_backup`/`pointer_backup` = profil OpenAI
 * (kunci terpisah); mode penyedia memilih mana yang jadi Utama (M1).
 */
export const PROVIDER_FEATURES = [
	"chat",
	"chat_backup",
	"pointer",
	"pointer_backup",
	"voice",
] as const;
export type ProviderFeature = (typeof PROVIDER_FEATURES)[number];

export interface AssistantSettingsDto {
	enabled: boolean;
	assistantName: string;
	personaNote: string | null;
	dailyMessageLimitPerUser: number;
	dailyTokenLimitGlobal: number;
	ratePerMinutePerUser: number;
	maxInputChars: number;
	historyWindow: number;
	retentionDays: number;
	kioskUserId: string | null;
	dailyMessageLimitKiosk: number;
	guideAutoAdvanceSec: number;
	voiceLiveModel: string;
	voiceName: string | null;
	voiceReadExactInstruction: string | null;
	voiceDailyMinutesUser: number;
	voiceDailyMinutesKiosk: number;
	voiceSessionMaxMinutes: number;
	voiceIdleOffSeconds: number;
	/** Suara di /wall: off (bawaan) menolak akun kiosk di server. */
	wallVoiceMode: WallVoiceMode;
	/** Mode NOC (hanya berlaku bila wallVoiceMode = noc): perilaku mikrofon, volume 0–100, ekor hening ms. */
	wallNocMicMode: WallNocMicMode;
	wallNocVolume: number;
	wallNocSettleMs: number;
	/** Plafon AI (06c, C1: hanya disimpan & ditampilkan, belum ditegakkan). 0 = tanpa batas. */
	chatTokenLimitMonthly: number;
	voiceMinutesLimitDaily: number;
	voiceMinutesLimitMonthly: number;
	chatUsdLimitDaily: number;
	chatUsdLimitMonthly: number;
	voiceUsdLimitDaily: number;
	voiceUsdLimitMonthly: number;
	capWarnPercent: number;
}

/** Status kunci tersimpan: `needs-reentry` = gagal didekripsi (AI_CREDENTIALS_KEY berganti). */
export type ApiKeyStatus = "set" | "missing" | "needs-reentry";

export interface ProviderSlotDto {
	feature: ProviderFeature;
	enabled: boolean;
	label: string | null;
	providerType: string;
	baseUrl: string | null;
	model: string | null;
	temperature: number | null;
	maxTokens: number | null;
	timeoutMs: number;
	apiKeyStatus: ApiKeyStatus;
	apiKeyHint: string | null;
	lastTestAt: string | null;
	lastTestOk: boolean | null;
}

export interface AssistantTodayStatsDto {
	messages: number;
	tokens: number;
	activeUsers: number;
	lastErrorAt: string | null;
}

export interface KioskCandidateDto {
	id: string;
	name: string;
}

/** Respons `GET /api/admin/ai-assistant`. */
export interface AiAssistantAdminOverviewDto {
	settings: AssistantSettingsDto;
	providers: ProviderSlotDto[];
	today: AssistantTodayStatsDto;
	kioskCandidates: KioskCandidateDto[];
	/** Mode penyedia AI teks + kesiapan profil + model OpenAI belum bertarif. */
	providerMode: ProviderModeStatusDto;
	/** false bila `AI_CREDENTIALS_KEY` kosong/tidak valid — API key tidak bisa disimpan. */
	cryptoConfigured: boolean;
	/**
	 * Retensi isi transkrip (hari) yang sedang dipakai job penghapus
	 * (`assistantTranscriptRetentionDays()`, env `ASSISTANT_TRANSCRIPT_RETENTION_DAYS`).
	 * Hanya untuk ditampilkan — tidak bisa diubah lewat form pengaturan. Berbeda
	 * dari `settings.retentionDays`, yang menghapus percakapan tidak aktif.
	 */
	transcriptRetentionDays: number;
}

/** Body `PUT /api/admin/ai-assistant/settings`. */
export type AssistantSettingsUpdate = AssistantSettingsDto;

/**
 * Body `PUT /api/admin/ai-assistant/providers/:feature`.
 * `apiKey`: tidak dikirim = pertahankan, `""` = hapus, string = ganti.
 */
export interface ProviderSlotUpdate {
	enabled: boolean;
	label: string | null;
	baseUrl: string | null;
	model: string | null;
	temperature: number | null;
	maxTokens: number | null;
	timeoutMs: number;
	apiKey?: string;
}

/** Respons `POST /api/admin/ai-assistant/providers/:feature/test`. */
export interface ProviderTestResultDto {
	ok: boolean;
	latencyMs: number | null;
	/** Pesan yang sudah disaring server — tanpa header/kunci. */
	error: string | null;
}
